import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
  Chip,
  IconButton,
} from '@mui/material';
import { Search, X, RefreshCw, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../services/api';
import AddFAQModal from '../../components/admin/AddFAQModal';
import EditFAQModal from '../../components/admin/EditFAQModal';

export default function FAQ() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = parseInt(process.env.REACT_APP_PAGINATION_LIMIT || '10', 10);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, faq: null });
  const [actionLoading, setActionLoading] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedFAQ, setSelectedFAQ] = useState(null);

  const { control, handleSubmit, watch, reset } = useForm({
    defaultValues: {
      searchQuery: '',
    },
  });

  const searchQuery = watch('searchQuery');

  // State to track the actual search value being used for API calls
  const [appliedSearchQuery, setAppliedSearchQuery] = useState('');

  const fetchFAQs = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        page_size: pageSize,
      };

      if (appliedSearchQuery && appliedSearchQuery.trim()) {
        params.search = appliedSearchQuery.trim();
      }

      const response = await api.admin.getFAQs(params);
      setFaqs(response.faqs || []);
      setTotal(response.total || 0);
    } catch (error) {
      console.error('Failed to fetch FAQs:', error);
      toast.error('Failed to fetch FAQs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFAQs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, appliedSearchQuery]);

  const onSubmit = () => {
    // Apply the search value and reset to page 1
    setAppliedSearchQuery(searchQuery);
    setPage(1);
  };

  const handleClearSearch = () => {
    reset({
      searchQuery: '',
    });
    setAppliedSearchQuery('');
    setPage(1);
  };

  const handlePageChange = (_event, value) => {
    setPage(value);
  };

  const handleRefresh = () => {
    fetchFAQs();
  };

  const handleOpenDeleteDialog = (faq) => {
    setDeleteDialog({ open: true, faq });
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialog({ open: false, faq: null });
  };

  const handleDelete = async () => {
    const { faq } = deleteDialog;
    setActionLoading(faq.id);

    try {
      await api.admin.deleteFAQ(faq.id);
      toast.success('FAQ deleted successfully');
      handleCloseDeleteDialog();
      fetchFAQs();
    } catch (error) {
      console.error('Failed to delete FAQ:', error);
      toast.error('Failed to delete FAQ');
    } finally {
      setActionLoading(null);
    }
  };

  const handleEdit = (faq) => {
    setSelectedFAQ(faq);
    setEditModalOpen(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">FAQ Management</h1>
        <div className="flex gap-2">
          <Button
            variant="outlined"
            startIcon={<RefreshCw className="w-4 h-4" />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Plus className="w-4 h-4" />}
            onClick={() => setAddModalOpen(true)}
          >
            Add FAQ
          </Button>
        </div>
      </div>

      {/* Search Bar */}
      <Paper className="p-4">
        <form onSubmit={handleSubmit(onSubmit)} className="flex gap-2">
          <Controller
            name="searchQuery"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                placeholder="Search FAQs..."
                variant="outlined"
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: <Search className="w-4 h-4 mr-2 text-gray-400" />,
                  endAdornment: searchQuery && (
                    <IconButton size="small" onClick={handleClearSearch}>
                      <X className="w-4 h-4" />
                    </IconButton>
                  ),
                }}
              />
            )}
          />
          <Button type="submit" variant="contained">
            Search
          </Button>
        </form>
      </Paper>

      {/* FAQ Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Question</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Updated At</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                </TableRow>
              ))
            ) : faqs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No FAQs found
                </TableCell>
              </TableRow>
            ) : (
              faqs.map((faq) => (
                <TableRow key={faq.id}>
                  <TableCell>{faq.question || 'N/A'}</TableCell>
                  <TableCell>{faq.category || 'N/A'}</TableCell>
                  <TableCell>
                    <Chip
                      label={faq.active ? 'Active' : 'Inactive'}
                      color={faq.active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDate(faq.updated_at)}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleEdit(faq)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        onClick={() => handleOpenDeleteDialog(faq)}
                        disabled={actionLoading === faq.id}
                      >
                        {actionLoading === faq.id ? 'Deleting...' : 'Delete'}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center">
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
          />
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete FAQ</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this FAQ entry?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add FAQ Modal */}
      <AddFAQModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSuccess={fetchFAQs}
      />

      {/* Edit FAQ Modal */}
      <EditFAQModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        faq={selectedFAQ}
        onSuccess={fetchFAQs}
      />
    </div>
  );
}

